import { FC, useState } from "react"
import { toast } from "react-toastify"

import { useTranslation } from "react-i18next"

import { Button } from "components/buttons/Button"
import { ModalConfirmDestructive } from "components/modals/ModalConfirmDestructive"

import { usePermissions } from "contexts/PermissionsContext"

import { PERMISSIONS } from "constants/permissions"

import { settingsService } from "services/settings/settings.service"

import { useResetTargets } from "./useResetTargets"

interface ResetToDefaultButtonProps {
  /** Registry id, e.g. `rcoin`. Must match a target served by `GET /settings/reset-targets`. */
  target: string
  /** Human name of the section, used in the button and the modal title. */
  label: string
  onDone?: () => void | Promise<void>
  /** Names the section in the button itself, for a section that owns more than one group. */
  showSectionInLabel?: boolean
  className?: string
}

/**
 * The only way a settings section may be reset.
 *
 * Styled as a destructive outline so it can never read as the section's Save button, and
 * always routed through a confirmation.
 */
export const ResetToDefaultButton: FC<ResetToDefaultButtonProps> = ({ target, label, onDone, showSectionInLabel = false, className }) => {
  const { t } = useTranslation("translation", { useSuspense: false })
  const { has } = usePermissions()
  const { describe } = useResetTargets()
  const [open, setOpen] = useState(false)
  const [resetting, setResetting] = useState(false)

  if (!has(PERMISSIONS.SETTINGS_RESET)) return null

  const handleConfirm = async () => {
    setResetting(true)
    try {
      const success = await settingsService.resetSetting(target)
      if (success) {
        toast.success(t("settings.reset.success", { section: label }) as string)
        await onDone?.()
        setOpen(false)
      } else {
        toast.error(t("settings.reset.failed", { section: label }) as string)
      }
    } finally {
      setResetting(false)
    }
  }

  return (
    <>
      <Button variant="error" className={className ?? "btn-outline"} onClick={() => setOpen(true)}>
        {showSectionInLabel ? t("settings.reset.buttonNamed", { section: label }) : t("settings.reset.button")}
      </Button>

      <ModalConfirmDestructive
        open={open}
        onClose={() => setOpen(false)}
        onConfirm={handleConfirm}
        loading={resetting}
        title={t("settings.reset.confirmTitle", { section: label })}
        description={describe(target, label)}
        consequences={[label]}
        confirmLabel={t("settings.reset.confirmAction")}
      />
    </>
  )
}
